import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Switch,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { hostname } from "../utils";

interface Toolset {
  id: string;
  instanceName: string;
  type: string;
  status?: string;
  parameters?: {
    description?: string;
    toolCount?: number;
    integrationCount?: number;
  };
}

interface ToolsetSelectProps {
  getToken: Function;
  eventListener: Function;
  companyId: string;
  value: string[];
  onValueChange: (value: string[]) => void;
  title?: string;
  sx?: any;
}

const ToolsetSelect: React.FC<ToolsetSelectProps> = ({
  getToken,
  eventListener,
  companyId,
  value = [],
  onValueChange,
  title = "Toolsets (Optional)",
  sx = {},
}) => {
  const [toolsets, setToolsets] = useState<Toolset[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (companyId) {
      loadToolsets();
    }
  }, [companyId]);

  useEffect(() => {
    if (toolsets.length > 0 && value.length > 0) {
      const availableIds = toolsets.map((t) => t.id);
      const validSelectedIds = value.filter((id) => availableIds.includes(id));

      if (validSelectedIds.length !== value.length) {
        console.log(
          "Filtering out unavailable toolsets:",
          value.filter((id) => !availableIds.includes(id))
        );
        onValueChange(validSelectedIds);
      }
    }
  }, [toolsets, value, onValueChange]);

  const loadToolsets = async () => {
    try {
      const token = await getToken();
      const response = await axios.get(
        `${hostname}/integrations/by-type/toolset`,
        {
          params: { companyId },
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setToolsets(response.data.items || []);
    } catch (error) {
      console.error("Failed to load toolsets:", error);
      eventListener({
        type: "SET_ERROR",
        error: "Failed to load toolsets",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (toolsetId: string) => {
    const currentIndex = value.indexOf(toolsetId);
    const newValue = [...value];

    if (currentIndex === -1) {
      newValue.push(toolsetId);
    } else {
      newValue.splice(currentIndex, 1);
    }

    onValueChange(newValue);
  };

  return (
    <FormControl component="fieldset" sx={{ width: "100%", ...sx }}>
      <FormLabel component="legend">{title}</FormLabel>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
        Connect toolsets to provide curated sets of tools to this agent
      </Typography>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : toolsets.length === 0 ? (
        <Alert severity="info" sx={{ mt: 1 }}>
          No toolsets configured. Create toolsets in the Integrations menu to
          curate specific tools for your agents.
        </Alert>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {toolsets.map((toolset) => (
            <Box
              key={toolset.id}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
                p: 1,
                borderRadius: 1,
                bgcolor: value.includes(toolset.id)
                  ? "action.selected"
                  : "transparent",
                "&:hover": {
                  bgcolor: "action.hover",
                },
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={value.includes(toolset.id)}
                    onChange={() => handleToggle(toolset.id)}
                  />
                }
                label={
                  <Box>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Typography variant="body2">
                        {toolset.instanceName}
                      </Typography>
                      {toolset.parameters?.toolCount !== undefined && (
                        <Chip
                          label={`${toolset.parameters.toolCount} tools`}
                          size="small"
                          variant="outlined"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Box>
                    {toolset.parameters?.description && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mt: 0.5 }}
                      >
                        {toolset.parameters.description}
                      </Typography>
                    )}
                  </Box>
                }
                sx={{ m: 0, flex: 1 }}
              />
            </Box>
          ))}
        </Box>
      )}
    </FormControl>
  );
};

export default ToolsetSelect;
